import React, { useEffect, useRef, useState } from "react";
import { Link as RouterLink, useSearchParams } from "react-router-dom";
import {
  ArrowLeft, FileEdit, Save, RotateCcw, Loader2, CheckCircle2,
} from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { api, formatApiErrorDetail } from "../lib/api";
import { useRefreshPageContent } from "../context/PageContentContext";
import RichTextEditor from "../components/RichTextEditor";

export default function AdminPageContent() {
  const { user } = useAuth();
  const refreshPublic = useRefreshPageContent();
  const [searchParams] = useSearchParams();
  const focusId = searchParams.get("focus");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/page-content");
      setItems(r.data);
      setError("");
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const onSaved = async () => {
    await Promise.all([load(), refreshPublic()]);
  };

  return (
    <div
      className="admin-chrome min-h-screen bg-[#F8FAFC] text-[var(--brand-ink)]"
      data-testid="page-admin-page-content"
    >
      <header className="border-b border-black/10 bg-white">
        <div className="mx-auto flex max-w-5xl items-center justify-between px-5 py-4 sm:px-8">
          <RouterLink
            to="/admin"
            className="inline-flex items-center gap-2 font-display text-sm font-semibold"
          >
            <ArrowLeft size={14} /> Admin
          </RouterLink>
          <span className="label-kicker text-[var(--brand-body)]">
            Page headings · {user?.name || user?.email}
          </span>
        </div>
      </header>

      <main className="mx-auto max-w-5xl px-5 py-12 sm:px-8">
        <div className="mb-8 border-b border-black/10 pb-6">
          <div className="flex items-center gap-2 text-[#6CABDD]">
            <FileEdit size={16} strokeWidth={2.5} />
            <span className="label-kicker">Page copy editor</span>
          </div>
          <h1 className="mt-2 font-display text-3xl font-black tracking-tight sm:text-4xl">
            Edit the words that greet visitors.
          </h1>
          <p className="mt-2 max-w-2xl text-sm text-[var(--brand-body)]">
            Change the kicker, headline and intro paragraph for each page.
            Changes go live immediately. Click <span className="font-bold">Reset</span>{" "}
            to roll a page back to its built-in copy.
          </p>
        </div>

        {error ? (
          <p
            className="mb-4 rounded-sm border-l-2 border-[#E63946] bg-[#E63946]/5 px-3 py-2 text-sm"
            data-testid="admin-page-content-error"
          >
            {error}
          </p>
        ) : null}

        {loading ? (
          <div className="flex items-center gap-2 text-[var(--brand-body)]">
            <Loader2 size={14} className="animate-spin" /> Loading…
          </div>
        ) : (
          <div className="space-y-6" data-testid="admin-page-content-list">
            {items.map((p) => (
              <PageCard
                key={p.id}
                page={p}
                focused={p.id === focusId}
                onSaved={onSaved}
                setError={setError}
              />
            ))}
          </div>
        )}
      </main>
    </div>
  );
}

function PageCard({ page, focused, onSaved, setError }) {
  const cardRef = useRef(null);
  const [form, setForm] = useState({
    kicker: page.kicker,
    title: page.title,
    intro: page.intro,
    intro_rich: page.intro_rich || "",
    tile_blurb: page.tile_blurb || "",
    kicker_color: page.kicker_color || "",
    title_color: page.title_color || "",
    empty_title: page.empty_title || "",
    empty_body: page.empty_body || "",
    hide_header: !!page.hide_header,
    subject_placeholder: page.subject_placeholder || "",
    message_placeholder: page.message_placeholder || "",
  });
  const [saving, setSaving] = useState(false);
  const [resetting, setResetting] = useState(false);
  const [savedFlash, setSavedFlash] = useState(false);

  // When ?focus=<id> matches this card, scroll into view and highlight briefly.
  useEffect(() => {
    if (focused && cardRef.current) {
      cardRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
      cardRef.current.classList.add("ring-2", "ring-[#6CABDD]");
      const t = setTimeout(() => {
        cardRef.current?.classList.remove("ring-2", "ring-[#6CABDD]");
      }, 2200);
      return () => clearTimeout(t);
    }
  }, [focused]);

  // Re-sync form when the page prop changes (e.g. after parent reload)
  useEffect(() => {
    setForm({
      kicker: page.kicker,
      title: page.title,
      intro: page.intro,
      intro_rich: page.intro_rich || "",
      tile_blurb: page.tile_blurb || "",
      kicker_color: page.kicker_color || "",
      title_color: page.title_color || "",
      empty_title: page.empty_title || "",
      empty_body: page.empty_body || "",
      hide_header: !!page.hide_header,
      subject_placeholder: page.subject_placeholder || "",
      message_placeholder: page.message_placeholder || "",
    });
  }, [
    page.kicker,
    page.title,
    page.intro,
    page.intro_rich,
    page.tile_blurb,
    page.kicker_color,
    page.title_color,
    page.empty_title,
    page.empty_body,
    page.hide_header,
    page.subject_placeholder,
    page.message_placeholder,
  ]);

  const setField = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const dirty =
    form.kicker !== page.kicker ||
    form.title !== page.title ||
    form.intro !== page.intro ||
    (form.intro_rich || "") !== (page.intro_rich || "") ||
    (form.tile_blurb || "") !== (page.tile_blurb || "") ||
    (form.kicker_color || null) !== (page.kicker_color || null) ||
    (form.title_color || null) !== (page.title_color || null) ||
    (form.empty_title || "") !== (page.empty_title || "") ||
    (form.empty_body || "") !== (page.empty_body || "") ||
    (!!form.hide_header) !== (!!page.hide_header) ||
    (form.subject_placeholder || "") !== (page.subject_placeholder || "") ||
    (form.message_placeholder || "") !== (page.message_placeholder || "");

  const flashSaved = () => {
    setSavedFlash(true);
    setTimeout(() => setSavedFlash(false), 1500);
  };

  const save = async (e) => {
    e?.preventDefault();
    setSaving(true);
    setError("");
    try {
      const payload = {
        kicker: form.kicker,
        title: form.title,
        intro: form.intro,
        tile_blurb: form.tile_blurb || "",
        kicker_color: form.kicker_color || "",
        title_color: form.title_color || "",
        hide_header: !!form.hide_header,
      };
      if (page.supports_rich_intro) {
        payload.intro_rich = form.intro_rich || "";
      }
      if (page.supports_empty_state) {
        payload.empty_title = form.empty_title || "";
        payload.empty_body = form.empty_body || "";
      }
      if (page.supports_contact_form_placeholders) {
        payload.subject_placeholder = form.subject_placeholder || "";
        payload.message_placeholder = form.message_placeholder || "";
      }
      await api.put(`/admin/page-content/${page.id}`, payload);
      await onSaved();
      flashSaved();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  const reset = async () => {
    if (!page.is_overridden) return;
    if (!window.confirm(`Reset "${page.label}" to its built-in copy?`)) return;
    setResetting(true);
    setError("");
    try {
      await api.delete(`/admin/page-content/${page.id}`);
      await onSaved();
      flashSaved();
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setResetting(false);
    }
  };

  return (
    <form
      ref={cardRef}
      onSubmit={save}
      className="overflow-hidden rounded-md border border-black/10 bg-white transition-shadow"
      data-testid={`admin-page-card-${page.id}`}
    >
      <div className="flex items-center justify-between gap-3 border-b border-black/10 bg-[#F8FAFC] px-6 py-3">
        <h3 className="font-display text-sm font-bold uppercase tracking-wide">
          {page.label}
        </h3>
        <div className="flex items-center gap-2">
          {page.is_overridden ? (
            <span className="label-kicker text-[#6CABDD]">Customised</span>
          ) : (
            <span className="label-kicker text-[var(--brand-body)]">Default</span>
          )}
          {savedFlash ? (
            <span className="inline-flex items-center gap-1 label-kicker text-[#16A34A]">
              <CheckCircle2 size={12} /> Saved
            </span>
          ) : null}
        </div>
      </div>

      <div className="space-y-4 p-6">
        <Labeled label={`Kicker · ${(form.kicker || "").length}/40`}>
          <input
            value={form.kicker}
            onChange={setField("kicker")}
            maxLength={40}
            data-testid={`page-${page.id}-kicker`}
            placeholder="Tiny label above the heading"
            className="block w-full rounded-md border border-black/15 bg-white px-3 py-2 font-display text-sm outline-none focus:border-[#0A0F17]"
          />
        </Labeled>

        <Labeled label={`Title · ${(form.title || "").length}/160 — use Enter for line breaks`}>
          <textarea
            rows={2}
            value={form.title}
            onChange={setField("title")}
            maxLength={160}
            data-testid={`page-${page.id}-title`}
            placeholder="The big heading"
            className="block w-full rounded-md border border-black/15 bg-white px-3 py-2 font-display text-base font-bold outline-none focus:border-[#0A0F17]"
          />
        </Labeled>

        {page.supports_rich_intro ? (
          <div>
            <span className="label-kicker text-[var(--brand-body)]">
              Intro (rich) — images, YouTube embeds &amp; links allowed
            </span>
            <p className="mt-1 text-xs text-[var(--brand-body)]">
              Replaces the plain intro on this page. Use the toolbar to add images
              from your library, paste YouTube URLs, or link out to other sites.
              Leave the editor empty to hide the intro entirely.
            </p>
            <div className="mt-2" data-testid={`page-${page.id}-intro-rich`}>
              <RichTextEditor
                value={form.intro_rich || ""}
                onChange={(v) => setForm((f) => ({ ...f, intro_rich: v }))}
                placeholder="Write the splash intro — drop in images, videos, links…"
              />
            </div>
          </div>
        ) : (
          <Labeled label={`Intro · ${(form.intro || "").length}/400 (leave empty to hide)`}>
            <textarea
              rows={3}
              value={form.intro}
              onChange={setField("intro")}
              maxLength={400}
              data-testid={`page-${page.id}-intro`}
              placeholder="Optional supporting paragraph below the heading"
              className="block w-full rounded-md border border-black/15 bg-white px-3 py-2 text-sm leading-relaxed outline-none focus:border-[#0A0F17]"
            />
          </Labeled>
        )}

        <Labeled
          label={`Tile blurb · ${(form.tile_blurb || "").length}/140 (optional — overrides the intro on the homepage Explore tile)`}
        >
          <textarea
            rows={2}
            value={form.tile_blurb}
            onChange={setField("tile_blurb")}
            maxLength={140}
            data-testid={`page-${page.id}-tile-blurb`}
            placeholder="Short one-liner shown under this page's name on the homepage Explore tile. Leave blank to reuse the intro."
            className="block w-full rounded-md border border-black/15 bg-white px-3 py-2 text-sm leading-relaxed outline-none focus:border-[#0A0F17]"
          />
        </Labeled>

        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <ColorField
            label="Kicker colour (blank = inherit theme accent)"
            value={form.kicker_color}
            onChange={(v) => setForm((f) => ({ ...f, kicker_color: v }))}
            testid={`page-${page.id}-kicker-color`}
          />
          <ColorField
            label="Title colour (blank = inherit ink)"
            value={form.title_color}
            onChange={(v) => setForm((f) => ({ ...f, title_color: v }))}
            testid={`page-${page.id}-title-color`}
          />
        </div>

        <label className="flex items-center gap-2 rounded-md border border-black/10 bg-[#F8FAFC] px-3 py-2 text-sm">
          <input
            type="checkbox"
            checked={!!form.hide_header}
            onChange={(e) =>
              setForm((f) => ({ ...f, hide_header: e.target.checked }))
            }
            data-testid={`page-${page.id}-hide-header`}
            className="h-4 w-4"
          />
          <span>
            <span className="font-semibold">Hide this page's header block.</span>{" "}
            <span className="text-[var(--brand-body)]">
              Turns off the kicker + title + intro line entirely so the page starts straight into its content.
            </span>
          </span>
        </label>

        {page.supports_empty_state ? (
          <div className="rounded-md border border-dashed border-black/15 bg-[#F8FAFC] p-4">
            <p className="label-kicker text-[var(--brand-body)]">Empty-state message</p>
            <p className="mt-1 text-xs text-[var(--brand-body)]">
              Shown when this page has no posts yet. Clear both lines to hide the message completely.
            </p>
            <div className="mt-3 space-y-3">
              <Labeled label={`Empty-state title · ${(form.empty_title || "").length}/160`}>
                <input
                  value={form.empty_title}
                  onChange={setField("empty_title")}
                  maxLength={160}
                  data-testid={`page-${page.id}-empty-title`}
                  placeholder="The archive is empty — for now."
                  className="block w-full rounded-md border border-black/15 bg-white px-3 py-2 font-display text-sm font-bold outline-none focus:border-[#0A0F17]"
                />
              </Labeled>
              <Labeled label={`Empty-state body · ${(form.empty_body || "").length}/400`}>
                <textarea
                  rows={2}
                  value={form.empty_body}
                  onChange={setField("empty_body")}
                  maxLength={400}
                  data-testid={`page-${page.id}-empty-body`}
                  placeholder="Jonathan is digging through old programmes…"
                  className="block w-full rounded-md border border-black/15 bg-white px-3 py-2 text-sm leading-relaxed outline-none focus:border-[#0A0F17]"
                />
              </Labeled>
            </div>
          </div>
        ) : null}

        {page.supports_contact_form_placeholders ? (
          <div className="rounded-md border border-dashed border-black/15 bg-[#F8FAFC] p-4">
            <p className="label-kicker text-[var(--brand-body)]">Contact form placeholders</p>
            <p className="mt-1 text-xs text-[var(--brand-body)]">
              The grey hint text shown inside the subject + message inputs before a visitor starts typing.
            </p>
            <div className="mt-3 space-y-3">
              <Labeled label={`Subject placeholder · ${(form.subject_placeholder || "").length}/200`}>
                <input
                  value={form.subject_placeholder}
                  onChange={setField("subject_placeholder")}
                  maxLength={200}
                  data-testid={`page-${page.id}-subject-placeholder`}
                  placeholder={page.default_subject_placeholder || "What's this about?"}
                  className="block w-full rounded-md border border-black/15 bg-white px-3 py-2 font-display text-sm outline-none focus:border-[#0A0F17]"
                />
              </Labeled>
              <Labeled label={`Message placeholder · ${(form.message_placeholder || "").length}/200`}>
                <textarea
                  rows={2}
                  value={form.message_placeholder}
                  onChange={setField("message_placeholder")}
                  maxLength={200}
                  data-testid={`page-${page.id}-message-placeholder`}
                  placeholder={page.default_message_placeholder || "Tip, feedback, takedown request — whatever it is, write away."}
                  className="block w-full rounded-md border border-black/15 bg-white px-3 py-2 text-sm leading-relaxed outline-none focus:border-[#0A0F17]"
                />
              </Labeled>
            </div>
          </div>
        ) : null}
      </div>

      <div className="flex flex-wrap items-center justify-end gap-2 border-t border-black/5 bg-white px-6 py-4">
        {page.is_overridden ? (
          <button
            type="button"
            onClick={reset}
            disabled={resetting || saving}
            data-testid={`page-${page.id}-reset`}
            className="inline-flex items-center gap-1.5 rounded-md border border-black/10 px-3 py-1.5 label-kicker hover:border-[#0A0F17] disabled:opacity-60"
          >
            {resetting ? <Loader2 size={12} className="animate-spin" /> : <RotateCcw size={12} />}
            Reset to default
          </button>
        ) : null}
        <button
          type="submit"
          disabled={!dirty || saving}
          data-testid={`page-${page.id}-save`}
          className="inline-flex items-center gap-2 rounded-md bg-[#0A0F17] px-4 py-2 font-display text-sm font-bold text-white disabled:opacity-50"
        >
          {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
          {saving ? "Saving…" : "Save changes"}
        </button>
      </div>
    </form>
  );
}

function Labeled({ label, children }) {
  return (
    <label className="block">
      <span className="label-kicker text-[var(--brand-body)]">{label}</span>
      <div className="mt-2">{children}</div>
    </label>
  );
}

function ColorField({ label, value, onChange, testid }) {
  const valid = !value || /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/.test(value);
  return (
    <Labeled label={label}>
      <div className="flex items-center gap-2">
        <input
          type="color"
          value={value || "#6CABDD"}
          onChange={(e) => onChange(e.target.value)}
          data-testid={`${testid}-picker`}
          className="h-10 w-14 cursor-pointer rounded-md border border-black/15 bg-white p-1"
        />
        <input
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="(default)"
          data-testid={testid}
          className={`block w-full rounded-md border bg-white px-3 py-2 font-mono text-xs outline-none focus:border-[#0A0F17] ${
            valid ? "border-black/15" : "border-[#E63946]"
          }`}
        />
        {value ? (
          <button
            type="button"
            onClick={() => onChange("")}
            data-testid={`${testid}-clear`}
            className="label-kicker text-[var(--brand-body)] underline-offset-2 hover:underline"
          >
            Clear
          </button>
        ) : null}
      </div>
    </Labeled>
  );
}
